/* eslint-disable camelcase */

/**
 * This is for all common "theme" variables that do not relate directly to color palettes or other "brand" specific styles
 *
 * Note: These variables were extracted from the original cp-theme theme, to keep our theme files as DRY as possible
 */

export const baseFontSize = 16; // px Everything else is REMs except maybe very thin lines, like borders.

// where is this used?
interface Border {
  radius: number;
  width: number;
}

export const border: Border = {
  radius: 4,
  width: 2,
};

interface Breakpoints {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}

export const breakpoints: Breakpoints = {
  xs: 321,
  sm: 544,
  md: 768,
  lg: 1012,
  xl: 1280,
};

// animation durations
interface Duration {
  short: number;
  medium: number;
  long: number;
}

export const duration: Duration = {
  short: 0.3,
  medium: 0.6,
  long: 1.0,
};

export const ToastDuration: Duration = {
  short: 5000,
  medium: 7000,
  long: 10000,
};

// Note: Moved primary font family into theme schema, currently only have constant for code type font here.
interface FontFamily {
  code: string;
}

export const fontFamily: FontFamily = {
  code: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
};

// Font sizes in 'rem' relative to 'baseFontSize'
export interface FontSize {
  base: number;
  h1: number;
  h2: number;
  h3: number;
  h4: number;
  h5: number;
  h6: number;
  p: number;
  text_12: number;
  text_14: number;
  text_20: number;
  text_24: number;
}

export const fontSize: FontSize = {
  base: 1.0,
  h1: 2.0,
  h2: 1.7,
  h3: 1.3,
  h4: 1.1,
  h5: 1.0,
  h6: 1.0,
  p: 0.875,
  text_12: 0.75,
  text_14: 0.875,
  text_20: 1.25,
  text_24: 1.5,
};

interface FontWeight {
  light: number;
  normal: number;
  bold: number;
}

export const fontWeight: FontWeight = {
  light: 300,
  normal: 400,
  bold: 500,
};

interface SpacingSizes {
  xxs: number;
  xs: number;
  s: number;
  sm: number;
  m: number;
  l: number;
  xls: number;
  xl: number;
  xxl: number;
}

interface Spacing extends SpacingSizes {
  absolute: SpacingSizes;
}

export const spacing: Spacing = {
  // Relative spacing in `rem`
  xxs: 0.125,
  xs: 0.25,
  s: 0.5,
  sm: 0.75, // 12px
  m: 1,
  l: 1.5,
  xls: 1.75,
  xl: 2,
  xxl: 4,
  // Absolute spacing in `px`
  absolute: {
    xxs: 2,
    xs: 4,
    s: 8,
    sm: 12,
    m: 16,
    l: 24,
    xls: 28,
    xl: 32,
    xxl: 64,
  },
};

export const iconSize: Spacing = {
  // Relative size in `rem`
  xxs: 1,
  xs: 1.125,
  s: 1.25,
  sm: 1.375, // 12px
  m: 1.5,
  l: 1.75,
  xls: 2,
  xl: 3,
  xxl: 4,
  // Absolute size in `px`
  absolute: {
    xxs: 16,
    xs: 18,
    s: 20,
    sm: 22,
    m: 24,
    l: 28,
    xls: 32,
    xl: 48,
    xxl: 64,
  },
};

// Where is this used? This appears like it must be for video dimensions.
// I'm just not sure it needs to be here in the theme

// common widescreen resolutions in px
// https://pacoup.com/2011/06/12/list-of-true-169-resolutions/
interface WidthHeight {
  width: number;
  height: number;
}

interface WidescreenResolutions {
  sm: WidthHeight;
  md: WidthHeight;
}

export const widescreenResolutions: WidescreenResolutions = {
  sm: {
    width: 384,
    height: 216,
  },
  md: {
    width: 512,
    height: 288,
  },
};

interface ZIndex {
  loader: number;
  sliding_menu_bg: number;
  sliding_menu: number;
  modal: number;
  toast: number;
  dropDown: number;
}

export const zIndex: ZIndex = {
  loader: 1,
  sliding_menu_bg: 8,
  sliding_menu: 9,
  modal: 1001,
  toast: 10,
  dropDown: 1002,
};

export default {
  baseFontSize,
  border,
  breakpoints,
  duration,
  fontFamily,
  fontSize,
  fontWeight,
  spacing,
  iconSize,
  widescreenResolutions,
  zIndex,
};
